import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { calculateAgeInMonths, getClientId, getLocationId } from '../../utils/index';
import { ALL_EVENTS_GROUP } from '../../constants';
import useSettings from '../useSettings';
import dayjs from 'dayjs';
import { User } from '../../types';

interface UseEventFetchingParams {
  selectedEventGroupId: string | null;
  selectedChild: { dob: string } | null;
  currentPage: number;
  searchQuery: string | null;
  user: User | null;
}

interface Event {
  id: number;
  name: string;
  description: string;
  status: number;
  capacity: number;
  minMonthsRestrictions: number;
  maxMonthsRestrictions: number;
  startDate: string;
  endDate: string;
  registrationStartDate: string;
  registrationEndDate: string;
  occupiedSeats: number;
}

interface UseEventFetchingReturn {
  loading: boolean;
  filteredEvents: Event[];
  totalPages: number;
  emptyDueToAge: boolean;
}

interface BuildEventsUrlOptions {
  baseUrl: string;
  limit: number;
  page: number;
  selectedEventGroupId: string | null;
  searchQuery: string | null;
  hideAfterRegistrationClose: boolean;
  locationId: string | null | undefined;
  ageInMonths?: number | null;
}

function buildEventsUrl({
  baseUrl,
  limit,
  page,
  selectedEventGroupId,
  searchQuery,
  hideAfterRegistrationClose,
  locationId,
  ageInMonths,
}: BuildEventsUrlOptions): string {
  let url = `${baseUrl}/events?filter.status=1&filter.isVisible=1`;
  url += `&limit=${limit}&page=${page}`;

  if (
    selectedEventGroupId &&
    !isNaN(Number(selectedEventGroupId)) &&
    selectedEventGroupId !== ALL_EVENTS_GROUP.id
  ) {
    url += `&filter.group=${selectedEventGroupId}`;
  }
  if (ageInMonths != null && ageInMonths > 0) {
    url += `&filter.ageInMonths=${ageInMonths}`;
  }
  if (searchQuery) {
    url += `&search=${encodeURIComponent(searchQuery)}`;
  }
  if (hideAfterRegistrationClose) {
    const now = dayjs().toISOString();
    url += `&filter.registrationStartDate=$lte:${now}`;
    url += `&filter.registrationEndDate=$gte:${now}`;
  }
  if (locationId) {
    url += `&filter.locationId=${locationId}`;
  }

  return url;
}

const useEventFetching = ({
  selectedEventGroupId,
  selectedChild,
  currentPage,
  searchQuery,
  user: _user,
}: UseEventFetchingParams): UseEventFetchingReturn => {
  const [loading, setLoading] = useState(true);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [emptyDueToAge, setEmptyDueToAge] = useState(false);
  const events_api_url = import.meta.env.VITE_EVENTS_API_URL;
  const client_id = getClientId();
  const locationId = getLocationId();
  const events_limit = 12;
  const { hideAfterRegistrationClose } = useSettings();
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastFetchParamsRef = useRef<string>('');

  useEffect(() => {
    // Create a unique key for current fetch parameters
    const fetchParamsKey = JSON.stringify({
      groupId: selectedEventGroupId,
      childDob: selectedChild?.dob || null,
      page: currentPage,
      search: searchQuery,
      hideAfter: hideAfterRegistrationClose,
    });

    // If parameters haven't changed, don't fetch
    if (fetchParamsKey === lastFetchParamsRef.current) {
      return;
    }

    // Update the last fetch params
    lastFetchParamsRef.current = fetchParamsKey;

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const fetchEvents = async () => {
      setLoading(true);
      setEmptyDueToAge(false);
      try {
        const ageInMonths = selectedChild
          ? calculateAgeInMonths(selectedChild.dob)
          : null;

        const url = buildEventsUrl({
          baseUrl: events_api_url,
          limit: events_limit,
          page: currentPage,
          selectedEventGroupId,
          searchQuery,
          hideAfterRegistrationClose,
          locationId,
          ageInMonths,
        });

        const response = await axios.get(url, {
          headers: { client_id: client_id },
          signal,
        });
        let events = response.data.data.data;
        events = events.sort((a: Event, b: Event) => {
          const dateA = new Date(a.startDate);
          const dateB = new Date(b.startDate);
          return dateA.getTime() - dateB.getTime();
        });

        setTotalPages(response.data.data.meta.totalPages);
        setFilteredEvents(events);

        const emptyReason = response.data?.data?.meta?.emptyReason;
        setEmptyDueToAge(events.length === 0 && emptyReason === 'age');
        setLoading(false);
      } catch (error: any) {
        if (error.name === 'CanceledError' || error.message === 'canceled') {
          // Request was canceled, don't update state
          return;
        }
        console.error('Error fetching events:', error);
        setFilteredEvents([]);
        setEmptyDueToAge(false);
        setLoading(false);
      }
    };

    fetchEvents();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    selectedEventGroupId,
    selectedChild,
    currentPage,
    searchQuery,
    hideAfterRegistrationClose,
  ]);

  return { loading, filteredEvents, totalPages, emptyDueToAge };
};

export default useEventFetching;
